import { BlogArticle, BlogArticleKey } from "@/app/types/BlogArticle";
import { ArticleBlockType } from "@/app/types/BlogArticleContent";
import { BlogCategory } from "@/app/types/BlogCategory";
import { BlogSlugs, PublicPaths } from "@/app/vars/Routes";

export const smallTeamCallAnalysisArticle: BlogArticle = {
  key: BlogArticleKey.SMALL_TEAM_CALL_ANALYSIS,
  slug: BlogSlugs.SMALL_TEAM_CALL_ANALYSIS,
  categoryId: BlogCategory.CALL_ANALYSIS,
  date: "2026-08-30",
  updated: "2026-09-29",
  readTime: 7,
  locales: {
    fr: {
      title: "Analyser ses appels quand on est seul, ou à trois",
      excerpt:
        "Les outils du marché sont vendus par sièges, avec des minimums d'équipe. En dessous, il ne reste rien. Pourtant c'est là que chaque appel compte le plus.",
      description:
        "Minimums de sièges, prix par utilisateur, pas de self-serve : pourquoi l'analyse d'appels échappe aux petites structures.",
      ctaTitle: "Commencez par un appel, pas par un contrat",
      ctaText:
        "Pas de siège à réserver, pas de minimum d'équipe. Importez un premier échange dans une fiche client, voyez ce qu'on en tire, et décidez ensuite. 100 crédits offerts, sans carte.",
      body: [
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Vous passez cinq appels par semaine. Vous savez que la moitié de ce que vous cherchez à comprendre sur votre marché s'y trouve, et vous savez aussi que vous n'en réécouterez jamais un seul.",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Alors vous regardez ce qui existe. Et vous tombez systématiquement sur le même mur : un prix par utilisateur et par mois, souvent un minimum de sièges, et un formulaire de démonstration à la place d'un bouton d'inscription.",
        },
        { type: ArticleBlockType.H2, text: "La catégorie n'a pas été conçue pour vous" },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Ce n'est pas une impression. Les outils d'intelligence conversationnelle ont été construits pour un problème de manager : uniformiser une équipe de quinze à cent commerciaux, repérer qui décroche, et aligner tout le monde sur ce que font les meilleurs.",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Ce problème existe, il est réel, et il vaut cher à résoudre. Simplement, ce n'est pas le vôtre. Quand vous êtes seul ou à trois, vous n'avez pas de dispersion d'équipe à corriger. Vous avez un marché à comprendre.",
        },
        {
          type: ArticleBlockType.LEAD,
          text: "Une équipe de vingt cherche à réduire l'écart entre ses commerciaux. Un fondateur cherche à savoir ce que son marché lui répète.",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Cette différence explique tout le reste : le modèle de prix, les minimums, l'absence d'inscription libre. Un produit vendu à des équipes se vend par sièges, et un siège n'a de sens que s'il y en a plusieurs.",
        },
        { type: ArticleBlockType.H2, text: "Ce que ça donne concrètement" },
        {
          type: ArticleBlockType.TABLE,
          head: ["Ce que vous rencontrez", "Ce que ça veut dire à votre échelle"],
          rows: [
            [
              "Un prix par utilisateur et par mois",
              "Vous payez une capacité d'équipe pour un seul carnet d'appels",
            ],
            [
              "Un minimum de sièges à l'entrée",
              "Le ticket d'entrée est fixé par une équipe que vous n'avez pas",
            ],
            [
              "Une démonstration obligatoire avant d'essayer",
              "Vous ne pouvez pas savoir si ça vous apporte quelque chose avant d'avoir parlé à quelqu'un",
            ],
            [
              "Un engagement annuel",
              "Vous vous engagez sur douze mois pour une question que vous voulez trancher en une semaine",
            ],
          ],
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Les prix publics de la catégorie se sont plutôt tendus ces dernières années, et les offres les plus connues se situent aujourd'hui bien au-dessus de cent euros par utilisateur et par mois, quand les entrées de gamme tournent autour de dix à vingt. Vérifiez les tarifs au moment où vous lisez, ils bougent vite, mais l'écart entre les deux mondes, lui, ne bouge pas.",
        },
        { type: ArticleBlockType.H2, text: "Le raisonnement qui coince" },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Le réflexe, à ce stade, est de se dire qu'on verra plus tard, quand il y aura une équipe. C'est exactement à l'envers.",
        },
        {
          type: ArticleBlockType.COMPARE,
          bad: {
            title: "Attendre d'avoir une équipe",
            items: [
              "Les enseignements des cent premiers appels sont perdus",
              "L'offre se figure sur des intuitions non vérifiées",
              "Les mots du marché ne sont jamais collectés",
              "Le jour où l'équipe arrive, il n'y a rien à lui transmettre",
            ],
          },
          good: {
            title: "Commencer maintenant",
            items: [
              "Chaque appel alimente ce que vous savez de votre cible",
              "L'offre se corrige sur ce qui a été dit, pas sur ce qui a été supposé",
              "Le vocabulaire des clients est disponible pour écrire",
              "Le premier commercial recruté hérite d'une matière, pas d'un discours",
            ],
          },
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Une équipe de vingt commerciaux passe des milliers d'appels : en rater cent n'a pas d'importance statistique. Pour vous, vos cent premiers appels sont la totalité de ce que vous savez de votre marché. C'est le moment où l'analyse vaut le plus, et c'est précisément celui où on vous la refuse.",
        },
        { type: ArticleBlockType.H2, text: "Ce qu'il faut chercher à cette taille" },
        {
          type: ArticleBlockType.LIST,
          items: [
            "**Un paiement à l'usage.** Vous payez les appels que vous analysez, pas des sièges que vous n'occupez pas.",
            "**Aucun minimum.** Un seul appel doit pouvoir être traité, sinon vous ne pouvez pas tester.",
            "**Une inscription sans passage obligé.** Si vous devez parler à un commercial pour voir le produit, ce produit ne vous est pas destiné.",
            "**Une sortie que vous relisez vraiment.** À votre échelle, un score n'a pas d'intérêt. Ce qui compte est de retrouver ce que chaque client a dit, avec ses mots, et ce qui revient d'un client à l'autre.",
            "**Un import volontaire.** Vous déposez l'appel que vous avez choisi, plutôt qu'un robot qui s'invite dans toutes vos réunions.",
          ],
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Le dernier point est celui qu'on sous-estime. Un outil d'équipe cherche l'exhaustivité, parce qu'un manager veut voir tout le monde. Seul, vous n'avez pas besoin de tout : vous avez besoin des dix appels qui comptent, et de ce qu'ils ont en commun.",
        },
        {
          type: ArticleBlockType.CALLOUT,
          title: "Là où Meidly aide",
          text: "Pas de siège, pas de minimum d'équipe, pas de démonstration obligatoire. Chaque appel, enregistré sur Google Meet avec l'extension Chrome ou importé en fichier audio ou vidéo, rejoint la fiche du client : synthèse, engagements, deals détectés, passages clés étiquetés.",
          linkLabel: "Voir les fiches clients",
          linkPath: PublicPaths.HOME,
        },
        { type: ArticleBlockType.H2, text: "En résumé" },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Si les outils d'analyse d'appels vous paraissent hors de portée, ce n'est pas parce que vous êtes trop petit. C'est parce qu'ils règlent un problème d'équipe et se vendent comme tel. Le vôtre est différent, il se règle avec dix appels bien lus, et il n'y a aucune raison d'attendre d'avoir recruté pour s'y mettre.",
        },
      ],
      faq: [
        {
          question: "Un outil d'analyse d'appels a-t-il un intérêt pour un indépendant ?",
          answer:
            "Oui, mais pas pour la même raison qu'une équipe. Une équipe cherche à réduire l'écart entre ses commerciaux. Un indépendant cherche ce que son marché lui répète : les mots employés, les objections qui reviennent, la situation dans laquelle les gens achètent. C'est ce qui aide à corriger le discours et l'offre.",
        },
        {
          question: "Pourquoi ces outils imposent-ils un minimum de sièges ?",
          answer:
            "Parce qu'ils ont été conçus pour un problème de management d'équipe et vendus par un commercial. Un minimum de sièges rend le contrat rentable à vendre. C'est cohérent pour l'éditeur, et cela ferme la porte aux petites structures.",
        },
        {
          question: "Combien d'appels faut-il pour en tirer quelque chose ?",
          answer:
            "Beaucoup moins qu'on ne croit. Une dizaine d'appels sur la même cible fait déjà ressortir des répétitions exploitables : les mêmes objections, les mêmes formulations, la même situation de départ. Le premier appel donne déjà des verbatims à réutiliser.",
        },
        {
          question: "Faut-il attendre d'avoir une équipe commerciale pour s'y mettre ?",
          answer:
            "Non, c'est l'inverse. Les premiers appels sont ceux qui portent le plus d'information, parce qu'ils sont tout ce que vous savez du marché. Et le jour où vous recrutez, vous transmettez une matière réelle plutôt qu'un discours reconstitué.",
        },
      ],
    },
    en: {
      title: "Analysing your calls when you are on your own, or three of you",
      excerpt:
        "The tools on the market are sold by seat, with team minimums. Below that, nothing is left. Yet that is exactly where every single call counts most.",
      description:
        "Seat minimums, per-user pricing, no self-serve: why call analysis is out of reach for small outfits, and what changes when you pay for what you use.",
      ctaTitle: "Start with a call, not with a contract",
      ctaText:
        "No seat to reserve, no team minimum. Import a first exchange into a customer file, see what comes out of it, and decide after that. 100 free credits, no card.",
      body: [
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "You take five calls a week. You know half of what you are trying to learn about your market sits in them, and you also know you will never listen back to a single one.",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "So you look at what exists. And you hit the same wall every time: a price per user per month, often a seat minimum, and a demo form where a sign-up button should be.",
        },
        { type: ArticleBlockType.H2, text: "The category was not built for you" },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "That is not a feeling. Conversation intelligence tools were built for a manager's problem: making a team of fifteen to a hundred reps consistent, spotting who is slipping, and pulling everyone toward what the best ones do.",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "That problem is real and expensive to solve. It is simply not yours. On your own or with two others, you have no team spread to correct. You have a market to understand.",
        },
        {
          type: ArticleBlockType.LEAD,
          text: "A team of twenty wants to close the gap between its reps. A founder wants to know what the market keeps repeating.",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "That difference explains all the rest: the pricing model, the minimums, the missing sign-up. A product sold to teams is sold by seat, and a seat only makes sense when there are several.",
        },
        { type: ArticleBlockType.H2, text: "What it looks like in practice" },
        {
          type: ArticleBlockType.TABLE,
          head: ["What you run into", "What it means at your scale"],
          rows: [
            ["A price per user per month", "You pay for team capacity against a single call log"],
            ["A seat minimum to get in", "The entry ticket is set by a team you do not have"],
            [
              "A mandatory demo before trying",
              "You cannot tell whether it helps you until you have spoken to someone",
            ],
            [
              "An annual commitment",
              "You commit for twelve months to settle a question you wanted answered in a week",
            ],
          ],
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Public pricing in the category has firmed up over the past few years, and the best-known offers now sit well above a hundred euros per user per month, while entry-level tools hover around ten to twenty. Check the current rates when you read this, they move fast, but the gap between the two worlds does not.",
        },
        { type: ArticleBlockType.H2, text: "The reasoning that trips people up" },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "The reflex at this point is to say you will look at it later, once there is a team. That is exactly backwards.",
        },
        {
          type: ArticleBlockType.COMPARE,
          bad: {
            title: "Waiting until you have a team",
            items: [
              "What the first hundred calls taught is lost",
              "The offer sets on unverified hunches",
              "The market's own words are never collected",
              "The day the team arrives, there is nothing to hand over",
            ],
          },
          good: {
            title: "Starting now",
            items: [
              "Every call feeds what you know about your target",
              "The offer is corrected on what was said, not on what was assumed",
              "The customers' vocabulary is available to write with",
              "The first rep you hire inherits material, not a pitch",
            ],
          },
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "A team of twenty reps takes thousands of calls: missing a hundred carries no statistical weight. Your first hundred calls are the whole of what you know about your market. That is when analysis is worth the most, and precisely when the category turns you away.",
        },
        { type: ArticleBlockType.H2, text: "What to look for at this size" },
        {
          type: ArticleBlockType.LIST,
          items: [
            "**Pay for what you use.** You pay for the calls you analyse, not for seats you do not fill.",
            "**No minimum.** A single call has to be processable, otherwise you cannot test anything.",
            "**Sign-up without a gatekeeper.** If you have to talk to a salesperson to see the product, that product is not aimed at you.",
            "**Output you actually read.** At your scale a score is of little use. What counts is finding what each customer said, in their own words, and what comes back from one customer to the next.",
            "**A deliberate import.** You bring in the call you chose, rather than a bot inviting itself to every meeting.",
          ],
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "The last point is the underrated one. A team tool chases completeness, because a manager wants to see everyone. On your own you do not need everything: you need the ten calls that matter, and what they have in common.",
        },
        {
          type: ArticleBlockType.CALLOUT,
          title: "Where Meidly helps",
          text: "No seat, no team minimum, no mandatory demo. Every call, recorded on Google Meet with the Chrome extension or imported as an audio or video file, joins the customer file: synthesis, commitments, detected deals, tagged key passages.",
          linkLabel: "See the customer files",
          linkPath: PublicPaths.HOME,
        },
        { type: ArticleBlockType.H2, text: "In short" },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "If call analysis tools feel out of reach, it is not because you are too small. It is because they solve a team problem and are sold as such. Yours is different, it is settled with ten calls read properly, and there is no reason to wait until you have hired to get to it.",
        },
      ],
      faq: [
        {
          question: "Is a call analysis tool worth it for a one-person business?",
          answer:
            "Yes, but not for the reason a team buys one. A team wants to close the gap between its reps. A solo operator wants what the market keeps repeating: the words used, the objections that come back, the situation people are in when they buy. That is what helps you correct the pitch and the offer.",
        },
        {
          question: "Why do these tools impose seat minimums?",
          answer:
            "Because they were designed for a team management problem and sold by a salesperson. A seat minimum makes the contract worth selling. That is coherent for the vendor, and it shuts the door on small outfits.",
        },
        {
          question: "How many calls do you need before it tells you anything?",
          answer:
            "Far fewer than people think. Ten calls on the same target already surface usable repetitions: the same objections, the same phrasings, the same starting situation. The very first call already gives you verbatims to reuse.",
        },
        {
          question: "Should you wait until you have a sales team?",
          answer:
            "No, the opposite. The first calls carry the most information, because they are everything you know about the market. And the day you hire, you hand over real material rather than a reconstructed pitch.",
        },
      ],
    },
  },
};
